import { Controller } from "@hotwired/stimulus";

/**
 * Event Request Dates Controller
 *
 * Watches start/end date inputs and shows a non-blocking warning when the
 * selected range includes weekends or organization-blocked dates (holidays).
 *
 * GR-3389 — which days count as the weekend is organization configuration
 * (Date#wday / getDay() numbers, 0 = Sunday). An empty list is how the
 * organization switches the weekend half of this warning off; blocked dates
 * are real configuration rather than a heuristic and keep warning either way.
 * The default reproduces the hardcoded Saturday/Sunday behaviour for any
 * render that does not pass the value.
 */
export default class extends Controller {
  static targets = ["warning", "warningText"];
  static values = {
    blockedDates: { type: Array, default: [] },
    weekendDays: { type: Array, default: [0, 6] },
  };

  connect() {
    this.startInput = this.element.querySelector(
      '[name="event_request[requested_start_time]"]'
    );
    this.endInput = this.element.querySelector(
      '[name="event_request[requested_end_time]"]'
    );

    // GR-1790 #34 — clear stale validation errors as soon as either date is
    // edited. The cross-field validator (`end_time_not_before_start_time`)
    // is server-side, so a failed submit leaves the error caption in the DOM
    // even after the user fixes the offending field. Without this listener
    // the user sees a misleading error until the next submit.
    this.boundClearStaleErrors = this.clearStaleErrors.bind(this);
    [this.startInput, this.endInput].filter(Boolean).forEach((input) =>
      input.addEventListener("input", this.boundClearStaleErrors)
    );

    // Run initial check for pre-populated dates
    this.checkDates();
  }

  disconnect() {
    [this.startInput, this.endInput].filter(Boolean).forEach((input) =>
      input.removeEventListener("input", this.boundClearStaleErrors)
    );
  }

  clearStaleErrors() {
    [this.startInput, this.endInput].filter(Boolean).forEach((input) => {
      // Primer drives the red border via invalid="true" on the input; the
      // aria-invalid mirror is set for assistive tech. Both must go or the
      // field still looks invalid after the user corrects it.
      input.removeAttribute("invalid");
      input.removeAttribute("aria-invalid");
      // The error caption renders as a sibling of the input inside the
      // FormControl wrapper with the FormControl-inlineValidation class.
      const wrap = input.closest(".FormControl");
      wrap?.querySelectorAll(".FormControl-inlineValidation").forEach((el) => el.remove());
    });
  }

  checkDates() {
    const startStr = this.startInput?.value;
    const endStr = this.endInput?.value || startStr;

    if (!startStr) {
      this.hideWarning();
      return;
    }

    const start = new Date(startStr + "T00:00:00");
    const end = new Date((endStr || startStr) + "T00:00:00");

    if (isNaN(start) || isNaN(end) || end < start) {
      this.hideWarning();
      return;
    }

    const blockedSet = new Set(this.blockedDatesValue);
    const weekendSet = new Set(this.weekendDaysValue.map(Number));
    const weekendDays = [];
    const blockedDays = [];
    const MAX_DAYS = 365;

    const current = new Date(start);
    let iterations = 0;
    while (current <= end && iterations < MAX_DAYS) {
      const dayOfWeek = current.getDay();
      const dateStr = this.formatDate(current);

      if (blockedSet.has(dateStr)) {
        blockedDays.push(this.formatDisplayDate(current));
      } else if (weekendSet.has(dayOfWeek)) {
        weekendDays.push(this.formatDisplayDate(current));
      }

      current.setDate(current.getDate() + 1);
      iterations++;
    }

    if (weekendDays.length === 0 && blockedDays.length === 0) {
      this.hideWarning();
      return;
    }

    const parts = [];
    if (weekendDays.length > 0) {
      parts.push(
        `${weekendDays.length} weekend ${weekendDays.length === 1 ? "day" : "days"} (${weekendDays.join(", ")})`
      );
    }
    if (blockedDays.length > 0) {
      parts.push(
        `${blockedDays.length} blocked ${blockedDays.length === 1 ? "date" : "dates"} (${blockedDays.join(", ")})`
      );
    }

    this.warningTextTarget.textContent = `Your date range includes ${parts.join(" and ")}. These days may not be bookable.`;
    this.showWarning();
  }

  hideWarning() {
    this.warningTarget.classList.add("d-none");
  }

  showWarning() {
    this.warningTarget.classList.remove("d-none");
  }

  /** Format Date as YYYY-MM-DD for comparison with blockedDates */
  formatDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  /** Format Date for display (e.g. "Sat, Mar 21") */
  formatDisplayDate(date) {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  }
}
